import React, { useState, useRef, useEffect } from 'react';
import { BookOpen, Plus, Trash2, Save, X, Eye, Edit2, Video, Upload, FileVideo, Loader2, ArrowLeft, Paperclip, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { postRequest, getRequest } from '../utils/networking';
import { apiUrls, imageUrl, videoUrl, baseUrl } from '../utils/url';
import ConfirmationPopup from './ConfirmationPopup';
import CurriculumPreviewModal from './CurriculumPreviewModal';

export default function CurriculumModal({
  isOpen,
  onClose,
  initialSyllabus = [],
  onSave,
  readOnly = false,
  courseId = null
}) {
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDescription, setNewModuleDescription] = useState('');
  const [newModuleVideo, setNewModuleVideo] = useState(null);
  const [newModuleDuration, setNewModuleDuration] = useState('');
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [deletingModuleId, setDeletingModuleId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingModuleOriginalData, setEditingModuleOriginalData] = useState(null);
  const [isUpdatingModule, setIsUpdatingModule] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [editPreviewVideoUrl, setEditPreviewVideoUrl] = useState(null);
  const [videoToPreview, setVideoToPreview] = useState(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedModuleForAttachment, setSelectedModuleForAttachment] = useState(null);
  const attachmentInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [editUploadProgress, setEditUploadProgress] = useState(0);
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showVariantDeleteConfirmation, setShowVariantDeleteConfirmation] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);
  const [isDeletingVariant, setIsDeletingVariant] = useState(false);
  const validateVideoFile = (file) => {
    // Video size restriction removed as requested
    return true;
  };

  const handleVideoSelection = (file, isEditMode = false) => {
    if (!file) return;
    if (!validateVideoFile(file)) {
      return;
    }
    
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      toast.warning('Please select a valid video file format (MP4, WebM, OGG, MOV, AVI)');
      return;
    }
    
    setNewModuleVideo(file);
    if (previewVideoUrl) {
      URL.revokeObjectURL(previewVideoUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setPreviewVideoUrl(previewUrl);
    
    toast.success(`Video "${file.name}" selected! (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
  };

  const getFullVideoUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;
    
    const normalized = String(path).replace(/\\/g, "/");
    
    // CDN-hosted video/asset paths
    if (normalized.includes("ecomtamil/")) {
      const clean = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoUrl}/${clean}`;
    }
    const assetsIndex = normalized.indexOf("assets/");
    if (assetsIndex !== -1) {
      const clean = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoUrl}/${clean}`;
    }
    const asstesIndex = normalized.indexOf("asstes/");
    if (asstesIndex !== -1) {
      const clean = normalized.substring(asstesIndex + 7).replace(/^\/+/, '');
      return `${videoUrl}/${clean}`;
    }
    
    // Raw UUID/filename — serve from CDN instead of API server
    const clean = normalized.replace(/^\/+/, '');
    return `${videoUrl}/coursevideos/${clean}`;
  };

  // Build correct attachment URL with file extension appended
  const getAttachmentUrl = (v) => {
    const rawPath = v.variant_url || v.url || v.path || v.fileurl || v.attachment_url;
    if (!rawPath) return null;
    if (String(rawPath).startsWith('http')) return rawPath;
    const ext = (v.filetype || v.type || '').toLowerCase();
    const normalized = String(rawPath).replace(/\\/g, "/").replace(/^\/+/, '');
    const fileName = ext && !normalized.toLowerCase().endsWith(`.${ext}`) ? `${normalized}.${ext}` : normalized;
    
    // If path contains known directory markers, serve from CDN
    if (normalized.includes("ecomtamil/")) {
      const extractedPath = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoUrl}/${extractedPath}`;
    }
    if (normalized.includes("assets/")) {
      const assetsIndex = normalized.indexOf("assets/");
      const extractedPath = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoUrl}/${extractedPath}`;
    }
    
    // Serve from CDN directly instead of API
    return `${videoUrl}/coursevideos/${fileName}`;
  };

  useEffect(() => {
    const fetchCurriculum = async () => {
      if (isOpen && courseId) {
        try {
          setIsLoading(true);
          
          const response = await postRequest(apiUrls.getcurriculams, { 
            courseid: courseId,
            id: courseId 
          });
          console.log("Fetch curriculum response:", response);
          let syllabusData = [];
          
          if (response.status === "S" && response.data && Array.isArray(response.data)) {
            syllabusData = response.data;
            if (response.message) {
              toast.success(response.message);
            }
          }
          else if (response && typeof response === 'object') {
            for (let key in response) {
              if (Array.isArray(response[key])) {
                syllabusData = response[key];
                break;
              }
            }
          }
          
          if (syllabusData && syllabusData.length > 0) {
            const processedModules = syllabusData.map((item, index) => ({
              curriculamid: item.curriculamid || item.id || null,
              title: item.title || item.module_title || item.name || `Module ${index + 1}`,
              courseid: item.courseid || courseId,
              description: item.description || item.module_description || '',
              videoUrl: item.url || item.video_url || item.videoUrl || '',
              duration: item.duration || '',
              orderid: item.orderid || index + 1,
              variants: item.variants || item.attachments || []
            }));
            
            setModules(processedModules);
          } else {
            setModules([]);
          }
          
        } catch (error) {
          console.error("Error fetching curriculum:", error);
          toast.error(error.response?.message || error.message || "Network error: Unable to fetch curriculum");
          setModules([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurriculum();
  }, [isOpen, courseId]);

  const refreshCurriculum = async () => {
    if (!courseId) return;
    try {
      const response = await postRequest(apiUrls.getcurriculams, { 
        courseid: courseId,
        id: courseId 
      });
      
      let syllabusData = [];
      if (response && Array.isArray(response)) {
        syllabusData = response;
      } else if (response && typeof response === 'object') {
        for (let key in response) {
          if (Array.isArray(response[key])) {
            syllabusData = response[key];
            break;
          }
        }
      }
      
      if (syllabusData && syllabusData.length > 0) {
        const processedModules = syllabusData.map((item, index) => ({
          curriculamid: item.curriculamid || item.id || null,
          title: item.title || item.module_title || item.name || `Module ${index + 1}`,
          courseid: item.courseid || courseId,
          description: item.description || item.module_description || '',
          videoUrl: item.url || item.video_url || item.videoUrl || '',
          duration: item.duration || '',
          orderid: item.orderid || index + 1,
          variants: item.variants || item.attachments || []
        }));
        setModules(processedModules);

        // Also sync videoToPreview if the preview modal is open
        setVideoToPreview(prev => {
          if (!prev) return prev;
          const updated = processedModules.find(m => m.curriculamid === prev.curriculamid);
          return updated ? { ...prev, ...updated } : prev;
        });
      }
    } catch (error) {
      console.error("Error refreshing curriculum:", error);
    }
  };

  useEffect(() => {
    if (isOpen && initialSyllabus && !courseId) {
      const processedModules = initialSyllabus.map((item, index) => {
        let moduleData = {
          curriculamid: item.curriculamid || item.id || null,
          title: '',
          description: '',
          videoUrl: '',
          duration: '',
          orderid: index + 1,
        };

        if (typeof item === 'object' && item !== null) {
          moduleData.title = item.title || '';
          moduleData.description = item.description || '';
          moduleData.videoUrl = item.url || '';
          moduleData.duration = item.duration || '';
          moduleData.orderid = item.orderid || index + 1;
          moduleData.curriculamid = item.curriculamid || item.id || null;
        } else if (typeof item === 'string') {
          moduleData.title = item;
        }

        return moduleData;
      });

      setModules(processedModules);
    }
  }, [initialSyllabus, isOpen, courseId]);

  useEffect(() => {
    return () => {
      if (previewVideoUrl) {
        URL.revokeObjectURL(previewVideoUrl);
      }
      if (editPreviewVideoUrl) {
        URL.revokeObjectURL(editPreviewVideoUrl);
      }
    };
  }, [previewVideoUrl, editPreviewVideoUrl]);

  const startEditing = (module) => {
    setEditingModuleOriginalData({
      title: module.title,
      description: module.description,
      duration: module.duration,
      video: null
    });
    
    setEditingModuleId(module.curriculamid);
    setNewModuleTitle(module.title);
    setNewModuleDescription(module.description);
    setNewModuleDuration(module.duration);
    setNewModuleVideo(null);
    
    if (editPreviewVideoUrl) {
      URL.revokeObjectURL(editPreviewVideoUrl);
      setEditPreviewVideoUrl(null);
    }
    
    setTimeout(() => {
      document.getElementById('add-module-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const cancelEditing = () => {
    resetForm();
    setEditingModuleId(null);
    setEditingModuleOriginalData(null);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      handleVideoSelection(file, !!editingModuleId);
    } else if (file) {
      toast.warning('Please select a valid video file');
    }
  };

  const resetForm = () => {
    setNewModuleTitle('');
    setNewModuleDescription('');
    setNewModuleDuration('');
    setNewModuleVideo(null);
    
    if (previewVideoUrl) {
      URL.revokeObjectURL(previewVideoUrl);
      setPreviewVideoUrl(null);
    }
    if (editPreviewVideoUrl) {
      URL.revokeObjectURL(editPreviewVideoUrl);
      setEditPreviewVideoUrl(null);
    }
    setUploadProgress(0);
    setIsUploading(false);
    setEditUploadProgress(0);
    setIsEditUploading(false);
  };

  const saveEditedModule = async () => {
    if (!editingModuleId) return;
    
    const trimmedTitle = newModuleTitle?.trim();
    if (!trimmedTitle) {
      toast.warning("Please enter a module title");
      return;
    }

    setIsUpdatingModule(true);
    if (newModuleVideo) {
      setIsEditUploading(true);
      setEditUploadProgress(0);
    }

    try {
      const formData = new FormData();
      formData.append('curriculamid', editingModuleId);
      formData.append('courseid', courseId);
      formData.append('title', trimmedTitle);
      formData.append('description', newModuleDescription?.trim() || '');
      formData.append('duration', newModuleDuration?.trim() || '0');
      
      const moduleToUpdate = modules.find(m => m.curriculamid === editingModuleId);
      formData.append('orderid', moduleToUpdate?.orderid || 0);

      if (newModuleVideo) {
        if (!validateVideoFile(newModuleVideo)) {
          setIsUpdatingModule(false);
          setIsEditUploading(false);
          return;
        }
        formData.append('video', newModuleVideo);
      }

      let progressInterval;
      if (newModuleVideo) {
        progressInterval = setInterval(() => {
          setEditUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);
      }

      const response = await postRequest(apiUrls.updatecurriculam, formData);

      if (progressInterval) clearInterval(progressInterval);

      if (response.status === "S" || response.status === "s") {
        if (response.percent === 100) {
          setEditUploadProgress(100);
          toast.success("Video uploaded successfully!");
        }
        
        const successMessage = response.message || response.msg || "Module updated successfully!";
        toast.success(successMessage);
        
        const updatedModules = modules.map(module =>
          module.curriculamid === editingModuleId
            ? {
                ...module,
                title: trimmedTitle,
                description: newModuleDescription?.trim() || '',
                duration: newModuleDuration?.trim() || '0',
                videoUrl: response.data?.video_url || module.videoUrl,
              }
            : module
        );
        setModules(updatedModules);
        resetForm();
        setEditingModuleId(null);
        setEditingModuleOriginalData(null);
      } else {
        const errorMessage = response.message || response.msg || "Failed to update module";
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating module:", error);
      const errorMessage = error.response?.message || error.message || "Failed to update module. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsUpdatingModule(false);
      setIsEditUploading(false);
      setEditUploadProgress(0);
    }
  };

  const callCreateCurriculumAPI = async (moduleData, videoFile = null) => {
    try {
      const formData = new FormData();
      
      console.log("Preparing to call createcurriculam API with courseId:", courseId);
      
      formData.append('courseid', String(courseId));
      formData.append('title', moduleData.title);
      formData.append('description', moduleData.description || '');
      formData.append('duration', moduleData.duration || '0');
      formData.append('orderid', String(moduleData.orderid || modules.length + 1));
      
      if (videoFile && videoFile instanceof File) {
        if (!validateVideoFile(videoFile)) {
          throw new Error("Invalid video file");
        }
        formData.append('video', videoFile);
        
        console.log("Video File details:", {
          name: videoFile.name,
          size: videoFile.size,
          type: videoFile.type,
          sizeInMB: (videoFile.size / (1024 * 1024)).toFixed(2)
        });
      }
      
      const response = await postRequest(apiUrls.createcurriculams, formData);
      const responseprogress = await getRequest(apiUrls.testing);
      
      console.log("Create curriculum response:", response);
      console.log("Progress response:", responseprogress);
      if (responseprogress && responseprogress.percent === 100) {
        setUploadProgress(100);
        toast.success("Video upload completed!");
      }
      
      if (response.status === "S" || response.status === "s") {
        const successMessage = response.message || response.msg || "Module added successfully!";
        toast.success(successMessage);
        return response.data;
      } else {
        const errorMessage = response.message || response.msg || "Failed to add module";
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error in callCreateCurriculumAPI:", error);
      const errorMessage = error.response?.message || error.message || "Failed to add module. Please try again.";
      toast.error(errorMessage);
      throw error;
    }
  };

  const confirmDeleteModule = (index) => {
    const module = modules[index];
    if (!module.curriculamid) {
      toast.warning("Please wait for module to be fully saved before deleting");
      return;
    }
    setModuleToDelete({ index, module });
    setShowDeleteConfirmation(true);
  };

  const handleDeleteModule = async () => {
    if (!moduleToDelete) return;
    
    const { index, module } = moduleToDelete;
    setShowDeleteConfirmation(false);
    
    if (module.curriculamid) {
      try {
        setDeletingModuleId(module.curriculamid);
        
        const deletePayload = {
          curriculamid: module.curriculamid,
          courseid: courseId
        };
        console.log('deletepayload',deletePayload)
        const response = await postRequest(apiUrls.deletecurriculam, deletePayload);
        
        if (response.status === "S" || response.status === "s" || response.success === true) {
          const successMessage = response.message || response.msg || `"${module.title}" deleted successfully!`;
          toast.success(successMessage);
          setModules(prevModules => prevModules.filter((_, i) => i !== index));
        } else {
          const errorMessage = response.message || response.msg || "Failed to delete module";
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error("Error deleting module:", error);
        const errorMessage = error.response?.message || error.message || "Failed to delete module. Please try again.";
        toast.error(errorMessage);
      } finally {
        setDeletingModuleId(null);
        setModuleToDelete(null);
      }
    } else {
      setModules(prevModules => prevModules.filter((_, i) => i !== index));
      toast.success("Module removed successfully!");
      setModuleToDelete(null);
    }
  };

  const cancelDeleteModule = () => {
    setShowDeleteConfirmation(false);
    setModuleToDelete(null);
  };

  const handleAddModule = async () => {
    const trimmedTitle = newModuleTitle?.trim();
    
    setIsAddingModule(true);
    if (newModuleVideo) {
      setIsUploading(true);
      setUploadProgress(0);
    }
    
    try {
      const moduleData = {
        title: trimmedTitle,
        description: newModuleDescription?.trim() || '',
        duration: newModuleDuration?.trim() || '0',
        orderid: modules.length + 1
      };
      
      console.log("Adding module with data:", moduleData);
      console.log("Video file object:", newModuleVideo);
      
      let progressInterval;
      if (newModuleVideo) {
        progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);
      }
      
      const apiResponse = await callCreateCurriculumAPI(moduleData, newModuleVideo);
      
      if (progressInterval) clearInterval(progressInterval);
      
      const newModule = {
        curriculamid: apiResponse?.curriculamid || apiResponse?.id || null,
        title: trimmedTitle,
        description: newModuleDescription?.trim() || '',
        duration: newModuleDuration?.trim() || '0',
        orderid: modules.length + 1,
        videoUrl: apiResponse?.video_url || apiResponse?.url || '',
      };
      
      setModules([...modules, newModule]);
      resetForm();
      
    } catch (error) {
      console.error("Error adding module:", error);
    } finally {
      setIsAddingModule(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveCurriculum = async () => {
    onClose();
    toast.success("Curriculum saved successfully!");
  };

  const handleCloseModal = () => {
    onClose();
    resetForm();
    setEditingModuleId(null);
    setEditingModuleOriginalData(null);
  };

  const handleAttachmentClick = (module) => {
    setSelectedModuleForAttachment(module);
    attachmentInputRef.current?.click();
  };

  const fetchModuleAttachments = async (curriculamid) => {
    try {
      const response = await postRequest(apiUrls.getvideoattachments, { videoid: curriculamid });
      console.log("Fetch attachments response:", response);
      let attachments = [];
      if (response && Array.isArray(response)) {
        attachments = response;
      } else if (response && Array.isArray(response.data)) {
        attachments = response.data;
      } else if (response && typeof response === 'object') {
        for (let key in response) {
          if (Array.isArray(response[key])) {
            attachments = response[key];
            break;
          }
        }
      }
      return attachments;
    } catch (error) {
      console.error("Error fetching module attachments:", error);
      return null;
    }
  };

  const handleAttachmentFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedModuleForAttachment) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, DOCX, PNG, or JPG.');
      return;
    }

    // Validate size (e.g., 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }

    const moduleForUpload = selectedModuleForAttachment;

    try {
      setIsUploadingAttachment(true);
      console.log("Uploading attachment for module:", moduleForUpload.curriculamid);
      
      const formData = new FormData();
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      formData.append('videoid', moduleForUpload.curriculamid);
      formData.append('filetype', fileExtension);
      formData.append('file', file);

      const response = await postRequest(apiUrls.addcurriculamvariant, formData);
      console.log("Attachment upload response:", response);

      if (response.status === "S" || response.status === "s") {
        toast.success(response.message || "Attachment added successfully!");

        // Try to fetch fresh attachments for this module
        const freshAttachments = await fetchModuleAttachments(moduleForUpload.curriculamid);

        if (freshAttachments !== null) {
          // Update modules list with fresh attachments
          setModules(prev => prev.map(m =>
            m.curriculamid === moduleForUpload.curriculamid
              ? { ...m, variants: freshAttachments }
              : m
          ));

          // Update videoToPreview if this module is currently being previewed
          setVideoToPreview(prev => {
            if (!prev || prev.curriculamid !== moduleForUpload.curriculamid) return prev;
            return { ...prev, variants: freshAttachments };
          });
        } else {
          // Fallback: refresh all curriculum data and sync videoToPreview
          await refreshCurriculum();
        }
      } else {
        toast.error(response.message || "Failed to add attachment");
      }
    } catch (error) {
      console.error("Error adding attachment:", error);
      toast.error("An error occurred while uploading the attachment.");
    } finally {
      setIsUploadingAttachment(false);
      setSelectedModuleForAttachment(null);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleDeleteVariant = (variant, module) => {
    setVariantToDelete({ variant, module });
    setShowVariantDeleteConfirmation(true);
  };

  const cancelDeleteVariant = () => {
    setShowVariantDeleteConfirmation(false);
    setVariantToDelete(null);
  };

  const confirmDeleteVariant = async () => {
    if (!variantToDelete) return;

    const { variant, module } = variantToDelete;
    
    try {
      setIsDeletingVariant(true);
      const vId = variant.curriculamvariantid || variant.cvid || variant.id || variant.variantid;
      
      const response = await postRequest(apiUrls.deletecurriculamvariant, { 
        variantid: vId,
        curriculamid: module.curriculamid
      });
      
      if (response.status === "S" || response.status === "s") {
        const successMessage = response.message || response.msg || "Attachment deleted successfully";
        toast.success(successMessage);
        
        // Update local state for the main list
        setModules(prev => prev.map(m => {
          if (m.curriculamid === module.curriculamid) {
            return {
              ...m,
              variants: m.variants.filter(v => (v.curriculamvariantid || v.cvid || v.id || v.variantid) !== vId)
            };
          }
          return m;
        }));
        
        // Update current preview modal state
        if (videoToPreview && videoToPreview.curriculamid === module.curriculamid) {
          setVideoToPreview(prev => ({
            ...prev,
            variants: prev.variants.filter(v => (v.curriculamvariantid || v.cvid || v.id || v.variantid) !== vId)
          }));
        }
        
        setShowVariantDeleteConfirmation(false);
        setVariantToDelete(null);
      } else {
        toast.error(response.message || response.msg || "Failed to delete attachment");
      }
    } catch (err) {
      console.error("Error deleting variant:", err);
      toast.error("An error occurred while deleting the attachment");
    } finally {
      setIsDeletingVariant(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="w-full bg-slate-50 flex flex-col h-full"
      >
        {/* Header - Matches the Edit Course page style */}
        <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleCloseModal}
                disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Course Syllabus
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveCurriculum}
                disabled={isAddingModule || deletingModuleId || editingModuleId !== null || isUpdatingModule}
                className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-bold flex items-center gap-2  cursor-pointer"
              >
                <Save size={16} />
                Save Syllabus
              </button>
              <button
                onClick={handleCloseModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                disabled={isAddingModule || deletingModuleId || isUpdatingModule}
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              
              {/* Left Column: Module Editor Card */}
              <div className="w-full lg:w-1/2 flex flex-col gap-6">
                <div className="bg-white rounded-[2rem] border border-slate-100  p-6 md:p-8 flex flex-col h-[500px]">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-50 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
                        {editingModuleId ? <Edit2 size={20} /> : <Plus size={20} />}
                      </div>
                      <h3 className="text-base font-bold text-slate-800">
                        {editingModuleId ? 'Edit Module' : 'Add New Module'}
                      </h3>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-6 mb-4">
                    {/* Title Input */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Module Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newModuleTitle}
                        onChange={(e) => setNewModuleTitle(e.target.value)}
                        placeholder="e.g., Introduction to Digital Marketing"
                        className="w-full text-sm border border-slate-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all placeholder:text-slate-400"
                        disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                      />
                    </div>

                    {/* Description Textarea */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Module Description
                      </label>
                      <textarea
                        value={newModuleDescription}
                        onChange={(e) => setNewModuleDescription(e.target.value)}
                        placeholder="What will students learn in this module?"
                        rows="4"
                        className="w-full text-sm border border-slate-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all placeholder:text-slate-400 resize-none"
                        disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                      />
                    </div>

                    {/* Duration Input */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Duration
                      </label>
                      <input
                        type="text"
                        value={newModuleDuration}
                        onChange={(e) => setNewModuleDuration(e.target.value)}
                        placeholder="e.g., 15 minutes"
                        className="w-full text-sm border border-slate-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all placeholder:text-slate-400"
                        disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                      />
                    </div>

                    {/* Video Upload Section */}
                    <div
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Module Video
                      </label>
                      <div className={`relative border-2 border-dashed rounded-3xl p-6 transition-all ${
                        isDragging ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-400 hover:bg-orange-50/30'
                      }`}>
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo"
                          onChange={(e) => handleVideoSelection(e.target.files[0], !!editingModuleId)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                        />
                        
                        <div className="flex flex-col items-center text-center">
                          {newModuleVideo ? (
                            <div className="w-full">
                              <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-100  mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
                                    <Video size={20} />
                                  </div>
                                  <div className="text-left">
                                    <p className="text-sm font-bold text-slate-800 truncate max-w-[200px]">{newModuleVideo.name}</p>
                                    <p className="text-[10px] text-slate-500">{(newModuleVideo.size / (1024 * 1024)).toFixed(2)} MB</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNewModuleVideo(null);
                                    if (previewVideoUrl) {
                                      URL.revokeObjectURL(previewVideoUrl);
                                      setPreviewVideoUrl(null);
                                    }
                                  }}
                                  className="p-2 text-slate-300 hover:text-red-500 transition-colors cursor-pointer"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 mb-3">
                                <Upload size={24} />
                              </div>
                              <p className="text-sm font-bold text-slate-800 mb-1">Upload Module Video</p>
                              <p className="text-xs text-slate-500 mb-4">Max file size: 2MB. Recommended format: MP4</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Upload Progress */}
                      {(isUploading || isEditUploading) && (
                        <div className="mt-4 bg-orange-50 rounded-2xl p-4 border border-orange-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-orange-600">Uploading Video...</span>
                            <span className="text-xs font-bold text-orange-600">{editingModuleId ? editUploadProgress : uploadProgress}%</span>
                          </div>
                          <div className="h-2 w-full bg-orange-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-orange-500 rounded-full transition-all duration-300"
                              style={{ width: `${editingModuleId ? editUploadProgress : uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Video Preview */}
                      {previewVideoUrl && !isUploading && !isEditUploading && (
                        <div className="mt-4 rounded-3xl overflow-hidden border border-slate-100 bg-black aspect-video flex items-center justify-center">
                          <video 
                            className="w-full h-full object-contain"
                            controls
                            playsInline
                            preload="auto"
                            src={previewVideoUrl}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-50 flex-shrink-0">
                    <button
                      onClick={editingModuleId ? cancelEditing : resetForm}
                      disabled={isAddingModule || isUpdatingModule}
                      className="px-6 py-2.5 text-white bg-black rounded-lg font-bold text-sm  cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={editingModuleId ? saveEditedModule : handleAddModule}
                      disabled={isAddingModule || deletingModuleId || isUpdatingModule}
                      className="px-8 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {(isAddingModule || isUpdatingModule) ? (
                        <><Loader2 size={18} className="animate-spin" /> {editingModuleId ? 'Updating...' : 'Adding...'}</>
                      ) : (
                        <>{editingModuleId ? <Save size={18} /> : <Plus size={18} />} {editingModuleId ? 'Update Module' : 'Add Module'}</>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column: Modules List Card */}
              <div className="w-full lg:w-1/2 flex flex-col gap-6">
                <div className="bg-white rounded-[2rem] border border-slate-100 p-6 md:p-8 flex flex-col h-[500px]">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-50">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500">
                        <BookOpen size={20} />
                      </div>
                      <h3 className="text-base font-bold text-slate-800"> Module List</h3>
                    </div>
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-[10px] font-bold">
                      {modules.length} Modules Total</span>
                  </div>

                  <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
                    {isLoading ? (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                        <Loader2 size={40} className="animate-spin text-orange-500 mb-4" />
                        <p className="font-medium">Fetching curriculum...</p>
                      </div>
                    ) : modules.length > 0 ? (
                      modules.map((module, index) => (
                        <div 
                          key={module.curriculamid || `temp-${index}`} 
                          className={`group bg-white rounded-2xl border p-3 transition-all duration-300 ${
                            editingModuleId === module.curriculamid 
                              ? 'border-orange-500' 
                              : 'border-slate-100 hover:border-orange-200'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                              editingModuleId === module.curriculamid ? 'bg-orange-500 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-orange-50 group-hover:text-orange-500'
                            }`}>
                              {index + 1}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-sm font-bold text-slate-800 truncate">{module.title || 'Untitled Module'}</h4>
                                <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => setVideoToPreview(module)}
                                    className={`p-1.5 rounded-lg transition-colors cursor-pointer ${(module.videoUrl || (module.variants && module.variants.length > 0)) ? 'text-blue-500 hover:bg-blue-50' : 'text-slate-300 cursor-not-allowed'}`}
                                    title="Preview Content"
                                    disabled={!(module.videoUrl || (module.variants && module.variants.length > 0))}
                                  >
                                    <Eye size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleAttachmentClick(module)}
                                    className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                                    title="Attach Files (PDF, DOCX, PNG, JPG)"
                                    disabled={isUploadingAttachment}
                                  >
                                    {isUploadingAttachment && selectedModuleForAttachment?.curriculamid === module.curriculamid ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                      <Paperclip size={14} />
                                    )}
                                  </button>

                                  <button
                                    onClick={() => startEditing(module)}
                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                    disabled={isAddingModule || deletingModuleId || editingModuleId !== null || !module.curriculamid}
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteModule(index)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                    disabled={isAddingModule || deletingModuleId === module.curriculamid || editingModuleId !== null || !module.curriculamid}
                                  >
                                    {deletingModuleId === module.curriculamid ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                  </button>
                                </div>
                              </div>
                              <p className="text-xs text-slate-500 line-clamp-2 mb-2 leading-relaxed">
                                {module.description || 'No description provided'}
                              </p>
                              
                              {/* Module Variants / Attachments */}
                              {module.variants && module.variants.length > 0 && (
                                <div className="mt-2 mb-3 flex flex-wrap gap-1.5">
                                  {module.variants.map((v, vIdx) => {
                                    const rawPath = v.variant_url || v.url || v.path || v.fileurl || v.attachment_url;
                                    const fileName = v.name || v.variant_name || rawPath || 'Attachment';
                                    let displayLabel = fileName.split('/').pop().split('\\').pop();
                                    const ext = (v.filetype || v.type || '').toLowerCase();
                                    if (ext && !displayLabel.toLowerCase().endsWith(`.${ext}`)) {
                                      displayLabel = `${displayLabel}.${ext}`;
                                    }
                                    const isPDF = ext === 'pdf' || /\.pdf$/i.test(displayLabel);
                                    return (
                                      <div 
                                        key={vIdx}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const url = getAttachmentUrl(v);
                                          window.open(url, '_blank');
                                        }}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 border border-slate-100 rounded-md text-[8px] font-bold text-slate-500 hover:text-orange-500 hover:bg-orange-50 transition-all cursor-pointer"
                                        title={`View/Download ${displayLabel}`}
                                      >
                                        <Paperclip size={8} className="text-slate-400" />
                                        <span className="truncate max-w-[100px]">{displayLabel}</span>
                                        <span className="text-[6px] opacity-60 uppercase">{isPDF ? 'PDF' : 'File'}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              <div className="flex items-center gap-4 text-[10px] font-bold text-slate-400">
                                <div className="flex items-center gap-1">
                                  <Video size={12} className="text-orange-400" />
                                  {module.videoUrl ? 'Video Included' : 'No Video'}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Loader2 size={12} className="text-blue-400" />
                                  {module.duration || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 text-center bg-slate-50/50 rounded-3xl border border-slate-100">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-slate-200 mb-4">
                          <BookOpen size={32} />
                        </div>
                        <h4 className="text-sm font-bold text-slate-800 mb-1">No Modules Yet</h4>
                        <p className="text-xs text-slate-500 max-w-[200px] mx-auto">Start building your course curriculum by adding modules on the left.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <input type="file"ref={attachmentInputRef}
        onChange={handleAttachmentFileChange} className="hidden" accept=".pdf,.docx,image/png,image/jpeg,image/jpg"/>

      <CurriculumPreviewModal
        videoToPreview={videoToPreview}
        onClose={() => setVideoToPreview(null)}
        getFullVideoUrl={getFullVideoUrl}
        handleDeleteVariant={handleDeleteVariant}
      />

      <ConfirmationPopup
        isOpen={showDeleteConfirmation}
        onClose={cancelDeleteModule}
        onConfirm={handleDeleteModule}
        title="Delete Module"
        message={`Are you sure you want to delete "${moduleToDelete?.module?.title || 'this module'}"? This action cannot be undone.`}
        confirmText="Yes, I am sure"
        cancelText="Cancel"
      />

      <ConfirmationPopup
        isOpen={showVariantDeleteConfirmation}
        onClose={cancelDeleteVariant}
        onConfirm={confirmDeleteVariant}
        title="Delete Attachment"
        message="Are you sure you want to delete this attachment? This action cannot be undone."
        confirmText="Yes, Delete it"
        cancelText="Cancel"
        isLoading={isDeletingVariant}
      />
    </>
  );
}